// src/pages/Landing.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight,
  Play,
  Sun,
  Moon,
  ChevronDown,
  FileText,
  Brain,
  Code,
  Zap,
  CheckCircle,
} from "lucide-react";
import Modal from "../components/Modal";
import heroImg from "../assets/meow-removebg-preview.png"; 

// === Animated Illustration Component ===
const AnimatedIllustration = ({ darkMode, offsetY = 18 }) => {
  useEffect(() => {
    if (!document.getElementById("float-keyframes")) {
      const style = document.createElement("style");
      style.id = "float-keyframes";
      style.innerHTML = `
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
          100% { transform: translateY(0px); }
        }
        .animate-float { animation: float 5s ease-in-out infinite; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <div className="flex justify-center items-center w-full">
      <div
        className="relative animate-float"
        style={{
          transform: `translateY(${offsetY}px)`,
          transition: "transform 0.4s ease-in-out",
        }}
      >
        {/* soft adaptive glow background */}
        <div
          className={`absolute inset-0 rounded-3xl blur-3xl ${
            darkMode
              ? "bg-blue-500/10 shadow-[0_0_40px_rgba(0,150,255,0.25)]"
              : "bg-blue-300/20 shadow-[0_0_40px_rgba(100,200,255,0.25)]"
          }`}
        ></div>

        {/* main hero image */}
        <img
          src={heroImg}
          alt="PaperMind AI Illustration"
          className="relative w-full max-w-md h-auto object-contain drop-shadow-2xl"
          style={{
            filter: darkMode
              ? "brightness(1) contrast(1.1)"
              : "brightness(1.05) contrast(1.02)",
          }}
        />
      </div>
    </div>
  );
};

// === Main Landing Page Component ===
const Landing = () => {
  const navigate = useNavigate();
  const navRef = useRef(null);

  const [darkMode, setDarkMode] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({
    features: false,
    resources: false,
    company: false,
  });

  // Initialize dark mode
  useEffect(() => {
    const isDark = localStorage.getItem("darkMode") === "true";
    setDarkMode(Boolean(isDark));
    if (isDark) document.documentElement.classList.add("dark");
  }, []);

  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem("darkMode", next.toString());
    if (next) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  };

  // Dropdown logic
  const toggleDropdown = (menu) => {
    setDropdownOpen((prev) => ({
      features: false,
      resources: false,
      company: false,
      [menu]: !prev[menu],
    }));
  };

  const closeAllDropdowns = () =>
    setDropdownOpen({ features: false, resources: false, company: false });

  useEffect(() => {
    const onClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target))
        closeAllDropdowns();
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const features = [
    {
      icon: <FileText className="w-8 h-8 text-primary-600" />,
      title: "Smart Paper Analysis",
      description:
        "Upload research papers and get instant, comprehensive summaries powered by AI.",
    },
    {
      icon: <Brain className="w-8 h-8 text-primary-600" />,
      title: "Key Insights Extraction",
      description:
        "Automatically identify methodology, findings, and critical conclusions.",
    },
    {
      icon: <Code className="w-8 h-8 text-primary-600" />,
      title: "Code Generation",
      description: "Generate working code implementations from research papers.",
    },
    {
      icon: <Zap className="w-8 h-8 text-primary-600" />,
      title: "Architecture Diagrams",
      description:
        "Visualize complex systems with auto-generated Mermaid diagrams.",
    },
  ];

  const benefits = [
    "Save hours of reading time",
    "Understand complex research faster",
    "Get ready-to-use code implementations",
    "Visualize architectures and workflows",
    "Export results to multiple formats",
  ];

  const dropdownMenus = {
    features: [
      {
        name: "Paper Analysis",
        description: "Analyze research papers",
      },
      {
        name: "Code Generation",
        description: "Generate code from papers",
      },
      {
        name: "Architecture Diagrams",
        description: "Visualize systems",
      }
      //{ name: "Admin Dashboard", link: "/admin", description: "View analytics" },
    ],
    resources: [
      { name: "Documentation", link: "/docs/PaperMind_Documentation.pdf", description: "Complete guides" },
      { name: "API Reference", link: "/docs/PaperMind_API_Reference_v2.pdf", description: "Developer docs" },
    ],
    company: [
      { name: "About Us", link: "/AboutUs", description: "Our story" },
      { name: "Contact", link: "/ContactUs", description: "Get in touch" },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* === Demo Modal === */}
      <Modal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)}>
        <iframe
          className="w-full h-full"
          src="https://www.youtube.com/embed/n2dVFdqMYGA"
          title="PaperMind AI Demo"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </Modal>

      {/* === NAVBAR === */}
      <nav
        ref={navRef}
        className="bg-white dark:bg-gray-800 shadow-md sticky top-0 z-50 w-full"
      >
        <div className="flex items-center justify-between h-16 w-full px-6 lg:px-12">
          {/* LEFT: Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center text-white font-bold">
                P
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                PaperMind AI
              </span>
            </Link>
          </div>

          {/* RIGHT: Nav Items */}
          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center space-x-1">
              {/* Features */}
              <div className="relative">
                <button
                  onClick={() => toggleDropdown("features")}
                  className="flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <span className="text-gray-700 dark:text-gray-300">Features</span>
                  <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </button>
                {dropdownOpen.features && (
                  <div className="absolute right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50 w-auto min-w-max">
                    {dropdownMenus.features.map((item, i) => (
                      <Link
                        key={i}
                        to={item.link}
                        className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {item.name}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {item.description}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Resources */}
              <div className="relative">
                <button
                  onClick={() => toggleDropdown("resources")}
                  className="flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <span className="text-gray-700 dark:text-gray-300">Resources</span>
                  <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </button>
                {dropdownOpen.resources && (
                  <div className="absolute right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50 w-auto min-w-max">
                    {dropdownMenus.resources.map((item, i) => (
                      <a
                        key={i}
                        href={`${process.env.PUBLIC_URL || ""}${item.link}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {item.name}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {item.description}
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Company */}
              <div className="relative">
                <button
                  onClick={() => toggleDropdown("company")}
                  className="flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <span className="text-gray-700 dark:text-gray-300">Company</span>
                  <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </button>
                {dropdownOpen.company && (
                  <div className="absolute right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50 w-auto min-w-max">
                    {dropdownMenus.company.map((item, i) => (
                      <Link
                        key={i}
                        to={item.link}
                        className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {item.name}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {item.description}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/pricing"
                className="px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
              >
                Pricing
              </Link>
            </div>

            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle dark mode"
            >
              {darkMode ? (
                <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              )}
            </button>

            {/* Sign In */}
            <button
              onClick={() => navigate("/login")}
              className="btn-secondary text-sm"
            >
              Sign In
            </button>
          </div>
        </div>
      </nav>

      {/* === HERO === */}
      <header className="container mx-auto px-4 py-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* LEFT: Hero text */}
          <div className="lg:col-span-6">
            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-6">
              PaperMind AI: From Papers to Progress
            </h1>

            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-xl">
              Analyze, summarize, and build from academic research instantly with AI that transforms complexity into clarity — empowering faster, smarter breakthroughs.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <button
                onClick={() => navigate("/signup")}
                className="btn-primary inline-flex items-center px-6 py-3 text-lg rounded-lg shadow"
              >
                <span>Start Free Trial</span>
                <ArrowRight className="w-5 h-5 ml-3" />
              </button>

              <button
                onClick={() => setShowDemoModal(true)}
                className="inline-flex items-center px-6 py-3 rounded-lg text-lg border border-gray-700 dark:border-gray-600 bg-white/3 hover:bg-white/6 transition-colors"
              >
                <Play className="w-4 h-4 mr-2" />
                <span>Watch Demo</span>
              </button>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
              {benefits.slice(0, 4).map((b, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-white bg-primary-600 rounded-full p-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {b}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Animated Illustration */}
          <div className="lg:col-span-6 flex justify-center lg:justify-end">
            <AnimatedIllustration darkMode={darkMode} offsetY={22} />
          </div>
        </div>
      </header>

      {/* === Features Section === */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
          Powerful Features
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((f, idx) => (
            <div
              key={idx}
              className="card hover:shadow-xl transition-shadow animate-slide-up"
              style={{ animationDelay: `${idx * 0.1}s` }}
            >
              <div className="mb-4">{f.icon}</div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {f.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {f.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* === CTA Section === */}
      <section className="bg-primary-600 dark:bg-primary-900 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-8">
              Why Researchers Love PaperMind AI
            </h2>
            <div className="space-y-4">
              {benefits.map((b, i) => (
                <div
                  key={i}
                  className="flex items-center justify-center space-x-3 text-white"
                >
                  <CheckCircle className="w-6 h-6 flex-shrink-0" />
                  <span className="text-lg">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === Final CTA === */}
      <section className="container mx-auto px-4 py-20 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
            Ready to Accelerate Your Research?
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Join thousands of researchers using PaperMind AI to understand papers faster.
          </p>
          <button
            onClick={() => navigate("/signup")}
            className="btn-primary text-lg px-8 py-3"
          >
            Start Free Trial
          </button>
        </div>
      </section>

      <footer className="bg-gray-100 dark:bg-gray-800 py-8 border-t border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4 text-center text-gray-600 dark:text-gray-400">
          <p>© 2025 PaperMind AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
